/**
 * usePushNotifications Hook
 * 
 * React hook for managing push notification permissions and settings
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { NotificationService } from '../services/NotificationService';
import { OneSignalService } from '../services/OneSignalService';

interface UsePushNotificationsReturn {
  isSupported: boolean;
  permission: NotificationPermission | null;
  isEnabled: boolean;
  subscriptionId: string | null;
  isLoading: boolean;
  error: string | null;
  requestPermission: () => Promise<NotificationPermission>;
  enablePushNotifications: () => Promise<void>;
  disablePushNotifications: () => Promise<void>;
  testNotification: () => Promise<void>;
}

export const usePushNotifications = (): UsePushNotificationsReturn => {
  const { user } = useAuth();
  const [permission, setPermission] = useState<NotificationPermission | null>(null);
  const [isEnabled, setIsEnabled] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const oneSignalService = OneSignalService.getInstance();
  const getDefaultPushPreferences = useCallback(() => ({
    enabled: true,
    orderUpdates: true,
    systemAlerts: true,
    reminders: true,
    achievements: true,
    quietHours: {
      enabled: false,
      startTime: '22:00',
      endTime: '08:00',
      timezone: 'America/Toronto'
    }
  }), []);
  const isSupported = typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator;

  // Check current permission status
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  // Check if push notifications are enabled for user
  useEffect(() => {
    const checkEnabled = async () => {
      if (!user) return;

      try {
        const notificationService = NotificationService.getInstance();
        const preferences = await notificationService.getUserPreferences(user.id);
        setIsEnabled(preferences?.push?.enabled || false);
      } catch (err) {
        console.error('Failed to check push notification preferences:', err);
      }
    };

    checkEnabled();
  }, [user]);

  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    setIsLoading(true);
    setError(null);

    try {
      const perm = await oneSignalService.requestPermission();
      setPermission(perm);
      return perm;
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to request notification permission';
      setError(errorMsg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [oneSignalService]);

  const enablePushNotifications = useCallback(async () => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    setIsLoading(true);
    setError(null);

    try {
      // Request permission first
      const perm = await requestPermission();
      
      if (perm !== 'granted') {
        throw new Error('Notification permission denied');
      }

      await oneSignalService.initialize();
      await oneSignalService.login(user.id);
      const playerId = await oneSignalService.getPushSubscriptionId();
      setSubscriptionId(playerId);

      // Register token with notification service
      const notificationService = NotificationService.getInstance();
      if (playerId) {
        await notificationService.registerFCMToken(user.id, playerId, 'web');
      }

      const existingPreferences = await notificationService.getUserPreferences(user.id);
      const currentPush = existingPreferences?.push || getDefaultPushPreferences();
      await notificationService.updateUserPreferences(user.id, {
        push: {
          ...currentPush,
          enabled: true
        }
      });

      setIsEnabled(true);
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to enable push notifications';
      setError(errorMsg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [user, oneSignalService, requestPermission, getDefaultPushPreferences]);

  const disablePushNotifications = useCallback(async () => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    setIsLoading(true);
    setError(null);

    try {
      const notificationService = NotificationService.getInstance();
      const existingPreferences = await notificationService.getUserPreferences(user.id);
      const currentPush = existingPreferences?.push || getDefaultPushPreferences();
      await notificationService.updateUserPreferences(user.id, {
        push: {
          ...currentPush,
          enabled: false
        }
      });

      setIsEnabled(false);
      setSubscriptionId(null);
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to disable push notifications';
      setError(errorMsg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [user, getDefaultPushPreferences]);

  const testNotification = useCallback(async () => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('Trigger a test notification via OneSignal dashboard or backend for user:', user.id);
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to send test notification';
      setError(errorMsg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  return {
    isSupported,
    permission,
    isEnabled,
    subscriptionId,
    isLoading,
    error,
    requestPermission,
    enablePushNotifications,
    disablePushNotifications,
    testNotification
  };
};

